// ============================================================================
// src/lib/backupStatusApi.ts — Client transport for Admin > Backups
// ============================================================================
// Calls the authenticated Netlify function `/.netlify/functions/backup-status`.
// Enforces bearer auth; supports abort signal for route-resource cancellation.
// Subject-scoped and private; validates response payload strictly without
// shallow-merging defaults that could manufacture operational facts.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import type {AdminBackupStatus} from './backupStatus.js';
import {validateBackupStatus} from './backupStatus.js';

export const BACKUP_STATUS_ENDPOINT = '/.netlify/functions/backup-status';

export type LoadBackupStatusOptions = {
  signal?: AbortSignal;
  force?: boolean;
};

export class BackupStatusError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, {status, code}: {status?: number; code?: string} = {}) {
    super(message);
    this.name = 'BackupStatusError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Fetch authenticated backup status from the server.
 * Requires active admin session; passes bearer token to server gate.
 */
export async function loadBackupStatus(
  sb: SupabaseClient,
  {signal, force = false}: LoadBackupStatusOptions = {},
): Promise<AdminBackupStatus> {
  if (!sb) {
    throw new BackupStatusError('Supabase client is required', {code: 'client_required'});
  }

  // Extract access token for the authenticated caller
  const {data: sessionData, error: sessionError} = await sb.auth.getSession();
  if (sessionError || !sessionData?.session?.access_token) {
    throw new BackupStatusError('Admin sign-in required to view backup status', {
      status: 401,
      code: 'unauthorized',
    });
  }

  const token = sessionData.session.access_token;
  const url = force ? `${BACKUP_STATUS_ENDPOINT}?_t=${Date.now()}` : BACKUP_STATUS_ENDPOINT;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      signal,
    });
  } catch (err) {
    if (signal?.aborted) {
      throw err;
    }
    throw new BackupStatusError(`Network failure contacting backup status endpoint: ${(err as Error).message}`, {
      code: 'network_error',
    });
  }

  if (res.status === 401) {
    throw new BackupStatusError('Unauthorized: session invalid or expired', {
      status: 401,
      code: 'unauthorized',
    });
  }
  if (res.status === 403) {
    throw new BackupStatusError('Forbidden: active admin role required to view backup status', {
      status: 403,
      code: 'forbidden',
    });
  }
  if (!res.ok) {
    let msg = `Server returned ${res.status}`;
    try {
      const errJson = await res.json();
      if (errJson && typeof errJson === 'object' && typeof errJson.message === 'string') {
        msg = errJson.message;
      }
    } catch {
      // ignore json parse error on non-ok response
    }
    throw new BackupStatusError(msg, {status: res.status, code: 'server_error'});
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new BackupStatusError('Non-JSON response from backup status endpoint', {code: 'malformed_response'});
  }

  try {
    return validateBackupStatus(json);
  } catch (err) {
    throw new BackupStatusError(`Malformed backup status payload: ${(err as Error).message}`, {
      code: 'invalid_payload',
    });
  }
}
